// src/pages/AdminFooterPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
  Link as MuiLink,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import {
  getFooterItems,
  createFooterItem,
  updateFooterItem,
  deleteFooterItem,
  FooterItem,
} from '../api/footerService';

// ── Section colour chips ─────────────────────────────────────────
const SECTION_COLOUR: Record<string, 'primary' | 'secondary' | 'success'> = {
  skills: 'primary',
  social: 'secondary',
  links:  'success',
};

// ── Suggested categories per section ────────────────────────────
const SECTION_CATEGORIES: Record<string, string[]> = {
  skills: [
    'Management', 'Software Engineering', 'Artificial Intelligence', 'Technologies',
  ],
  social: ['Social Media'],
  links:  ['Links'],
};

const SECTIONS = ['skills', 'social', 'links'];

// ── Empty form state ─────────────────────────────────────────────
const EMPTY_ITEM: Partial<FooterItem> = {
  section: 'skills',
  category: '',
  label: '',
  url: '',
  display_order: 0,
};

const AdminFooterPage: React.FC = () => {
  const { user } = useAuth();

  const [items, setItems]           = useState<FooterItem[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  // Create / edit dialog
  const [open, setOpen]               = useState(false);
  const [current, setCurrent]         = useState<Partial<FooterItem>>(EMPTY_ITEM);
  const [isEditing, setIsEditing]     = useState(false);

  // Delete confirmation dialog
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  // ── Data fetching ──────────────────────────────────────────────
  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await getFooterItems();
      setItems(data);
      setError('');
    } catch (err: any) {
      setError(`Failed to load footer items: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  // Auto-clear messages after 5 s
  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  // ── Dialog helpers ─────────────────────────────────────────────
  const handleOpenCreate = () => {
    setCurrent(EMPTY_ITEM);
    setIsEditing(false);
    setOpen(true);
  };

  const handleOpenEdit = (item: FooterItem) => {
    setCurrent({ ...item });
    setIsEditing(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrent(EMPTY_ITEM);
  };

  // ── Form change handlers ───────────────────────────────────────
  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrent(prev => ({ ...prev, [name]: value }));
  };

  const handleSelect = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    // When section changes, reset category
    if (name === 'section') {
      setCurrent(prev => ({ ...prev, section: value, category: '' }));
    } else {
      setCurrent(prev => ({ ...prev, [name]: value }));
    }
  };

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const payload = {
        ...current,
        url: current.url || null,
        display_order: Number(current.display_order ?? 0),
      };

      if (isEditing && current.id) {
        await updateFooterItem(current.id, payload);
        setSuccess('Footer item updated successfully');
      } else {
        await createFooterItem(payload as Omit<FooterItem, 'id'>);
        setSuccess('Footer item created successfully');
      }
      handleClose();
      fetchItems();
    } catch (err: any) {
      setError(`Failed to save footer item: ${err.message}`);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = (id: number) => {
    setItemToDelete(id);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete != null) {
      try {
        await deleteFooterItem(itemToDelete);
        setSuccess('Footer item deleted successfully');
        fetchItems();
      } catch (err: any) {
        setError(`Failed to delete footer item: ${err.message}`);
      }
    }
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  // ── Permissions ────────────────────────────────────────────────
  const canEdit   = user && ['admin', 'editor'].includes(user.role);
  const canDelete = user && user.role === 'admin';

  // ── Suggested categories for the currently selected section ───
  const suggestedCategories = SECTION_CATEGORIES[current.section ?? 'skills'] ?? [];

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Footer
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Add Item
            </Button>
          )}
        </Box>

        {/* Alerts */}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        {error   && <Alert severity="error"   sx={{ mb: 3 }}>{error}</Alert>}

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Section</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell align="center">Order</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Chip
                        label={item.section}
                        color={SECTION_COLOUR[item.section] ?? 'default'}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>{item.label}</TableCell>
                    <TableCell>
                      {item.url ? (
                        <MuiLink href={item.url} target="_blank" rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                          <LinkIcon fontSize="small" />
                          {item.url.length > 40 ? item.url.slice(0, 40) + '…' : item.url}
                        </MuiLink>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{item.display_order}</TableCell>
                    <TableCell>
                      {canEdit && (
                        <IconButton onClick={() => handleOpenEdit(item)} color="primary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton onClick={() => handleDelete(item.id!)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No footer items found. Click "Add Item" to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Create / Edit dialog ────────────────────────────────── */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Footer Item' : 'Add Footer Item'}</DialogTitle>
        <DialogContent>

          {/* Section */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="section-label">Section</InputLabel>
            <Select
              labelId="section-label"
              name="section"
              value={current.section ?? 'skills'}
              label="Section"
              onChange={handleSelect}
            >
              {SECTIONS.map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category — free-text with suggestions */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="category-label">Category (column heading)</InputLabel>
            <Select
              labelId="category-label"
              name="category"
              value={current.category ?? ''}
              label="Category (column heading)"
              onChange={handleSelect}
              // Allow typing a custom value via the TextField below
            >
              {suggestedCategories.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Custom category override */}
          <TextField
            margin="dense"
            label="Or type a custom category"
            name="category"
            fullWidth
            size="small"
            value={current.category ?? ''}
            onChange={handleInput}
            helperText="This text is shown as the column heading in the footer"
          />

          {/* Label */}
          <TextField
            margin="normal"
            label="Label"
            name="label"
            fullWidth
            required
            value={current.label ?? ''}
            onChange={handleInput}
            helperText="The text shown as a line item inside the column"
          />

          {/* URL */}
          <TextField
            margin="normal"
            label="URL (optional)"
            name="url"
            fullWidth
            value={current.url ?? ''}
            onChange={handleInput}
            helperText={
              current.section === 'skills'
                ? 'Leave blank — skill items do not have links'
                : 'Full URL including https://'
            }
            disabled={current.section === 'skills'}
          />

          {/* Display order */}
          <TextField
            margin="normal"
            label="Display order"
            name="display_order"
            type="number"
            fullWidth
            value={current.display_order ?? 0}
            onChange={handleInput}
            helperText="Lower numbers appear first within a section"
            inputProps={{ min: 0 }}
          />

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation dialog ───────────────────────────── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this footer item? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};


export default AdminFooterPage;
